<script setup lang="ts">
import { onBeforeMount, ref, inject, Ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { STATUS_OPEN, STATUS_CLOSED } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useViewComposable } from '@/views/composable';
import { useI18n } from 'vue-i18n';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ProgressSpinner from 'primevue/progressspinner';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';
import SelectDatesDisplay from '@/components/seasonal/SelectDatesDisplay.vue';
import SeasonDurationDisplay from '@/components/seasonal/SeasonDurationDisplay.vue';

const dialogRef = inject('dialogRef') as Ref;
const onApply = dialogRef?.value?.options?.emits?.onApply;
const onClose = dialogRef?.value?.options?.emits?.onClose;
const flightRules = dialogRef.value.options.data.flightRules;
const sheetData = dialogRef.value.options.data.sheetData;
const type = dialogRef.value.options.data.type;
const seasonal = dialogRef.value.options.data.seasonal;

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const instaquoteRuleFilters = ref();
const loading = ref(false);
const sheetDataFlight = ref<any>([]);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const sortOrder = ref(0);
const sortField = ref('validateMessage');
const isSort = ref(false);
const { checkValidity } = useComponentComposable();
const { styleColumnExport, renderNameFileExport } = useViewComposable();

const widthList = [10, 40, 40, 12, 12, 10, 10, 15, 15, 20, 20, 15, 10];
const sheetColumnStyle = styleColumnExport(widthList);

const initInstaQuoteRuleFilters = () => {
  instaquoteRuleFilters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ruleType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    desCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    gateCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    depDateStart: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    depDateEnd: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    ruleStatus: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initInstaQuoteRuleFilters();

onBeforeMount(() => {
  sheetDataFlight.value = [
    {
      name: 'Sheet 1',
      data: sheetData
    }
  ];
});

const handleImportFile = () => {
  onApply();
};

const handleRevert = () => {
  onApply();
};

const closeModal = () => {
  onClose();
};

const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
  }
};

const rowClass = (data: any) => {
  return [{ 'bg-error': data?.validateMessage }];
};

const handleShowMessage = (message: string) => {
  return message ?? t('common.labels.success');
};

const sortMessage = () => {
  isSort.value = !isSort.value;
  sortOrder.value = isSort.value ? -1 : 0;
};
</script>
<template>
  <div class="card view-flight-rule-modal">
    <DataTable v-model:filters="instaquoteRuleFilters" :rowClass="rowClass" :value="flightRules" paginator showGridlines :loading="loading" :rows="10" :sortField="sortField" :sortOrder="sortOrder" dataKey="id" filterDisplay="menu">
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <template #loading>
        <ProgressSpinner class="w-4rem h-4rem" strokeWidth="8" animationDuration="2s" />
      </template>
      <TableColumn field="ruleType" :header="$t('management.package.type')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <span class="long-text" v-tooltip="data[field]">
            {{ data[field] }}
          </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_type')" />
        </template>
      </TableColumn>
      <TableColumn field="gateCode" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <span class="white-space-nowrap">{{ $t('management.package.gateway') }} {{ $t('management.package.code') }}</span>
        </template>
        <template #body="{ data }">
          <div class="flex flex-col">
            <span>{{ data.gateCode }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_gateway')" />
        </template>
      </TableColumn>
      <TableColumn field="desCode" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <span class="white-space-nowrap">{{ $t('management.package.destination') }} {{ $t('management.package.code') }}</span>
        </template>
        <template #body="{ data }">
          <div class="flex flex-col">
            <span>{{ data.desCode }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination')" />
        </template>
      </TableColumn>
      <TableColumn field="minDays" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.min_days') }}</span>
            <span>({{ $t('management.package.departure_quote') }})</span>
          </div>
        </template>
        <template #body="{ data }">
          <div class="flex flex-col">
            <span>{{ data.minDay }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination')" />
        </template>
      </TableColumn>
      <TableColumn field="maxDays" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.max_days') }}</span>
            <span>({{ $t('management.package.departure_quote') }})</span>
          </div>
        </template>
        <template #body="{ data }">
          <div class="flex flex-col">
            <span>{{ data.maxDay }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination')" />
        </template>
      </TableColumn>
      <TableColumn field="minDuration" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.min_duration') }}</span>
          </div>
        </template>
      </TableColumn>
      <TableColumn field="maxDuration" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.max_duration') }}</span>
          </div>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="minPax" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.min_pax') }}</span>
          </div>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="maxPax" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.max_pax') }}</span>
          </div>
        </template>
      </TableColumn>
      <TableColumn field="minFClass" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.class') : $t('management.package.min_f') }}</span>
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.min_f') : $t('management.package.class') }} ( % )</span>
          </div>
        </template>
        <template #body="{ data, field }">
          <span>{{ data[field] }} %</span>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="maxYClass" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.class') : $t('management.package.max_y') }}</span>
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.max_y') : $t('management.package.class') }} ( % )</span>
          </div>
        </template>
        <template #body="{ data, field }">
          <span>{{ data[field] }} %</span>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="seasonalSummer" :showAddButton="false" :showFilterOperator="false" dataType="date">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ $t('common.labels.summer_season_dates') }}</span>
          </div>
        </template>
        <template #body="{ data }">
          <div v-if="data.seasonalSummer">
            <p class="mb-0 font-bold">
              {{
                checkValidity(data?.seasonalSummer)
                  ? formatDate(new Date(new Date().getFullYear(), +data?.seasonalSummer?.startMonth - 1, +data?.seasonalSummer?.startDay), 'dd-LLL', true, currentLanguage) +
                    ' ' +
                    $t('common.labels.to') +
                    ' ' +
                    formatDate(new Date(new Date().getFullYear(), +data?.seasonalSummer?.endMonth - 1, +data?.seasonalSummer?.endDay), 'dd-LLL', true, currentLanguage)
                  : $t('common.labels.default_date_text')
              }}
            </p>
            <SelectDatesDisplay class="my-2" :checkAvailableDate="checkValidity(data?.seasonalSummer)" :listDayOfWeek="data?.seasonalSummer ? data?.seasonalSummer?.dayOfWeeks : seasonal.seasonalSummer?.dayOfWeeks" />
            <SeasonDurationDisplay :checkAvailableDate="checkValidity(data?.seasonalSummer)" :durations="data.seasonalSummer ? data.seasonalSummer?.durations : seasonal.seasonalSummer?.durations" />
          </div>
        </template>
        <template #filter="{ filterModel }">
          <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('management.package.departure_date_start')">
            <template #footer>
              <p class="text-center">
                <small>{{ $t('common.press_space') }}</small>
              </p>
            </template>
          </DatePicker>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="seasonalWinter" :showAddButton="false" :showFilterOperator="false" dataType="date">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ $t('common.labels.winter_season_dates') }}</span>
          </div>
        </template>
        <template #body="{ data }">
          <div v-if="data.seasonalWinter">
            <p class="mb-0 font-bold">
              {{
                checkValidity(data?.seasonalWinter)
                  ? formatDate(new Date(new Date().getFullYear(), +data?.seasonalWinter?.startMonth - 1, +data?.seasonalWinter?.startDay), 'dd-LLL', true, currentLanguage) +
                    ' ' +
                    $t('common.labels.to') +
                    ' ' +
                    formatDate(new Date(new Date().getFullYear(), +data?.seasonalWinter?.endMonth - 1, +data?.seasonalWinter?.endDay), 'dd-LLL', true, currentLanguage)
                  : $t('common.labels.default_date_text')
              }}
            </p>
            <SelectDatesDisplay class="my-2" :checkAvailableDate="checkValidity(data?.seasonalWinter)" :listDayOfWeek="data?.seasonalWinter ? data?.seasonalWinter?.dayOfWeeks : seasonal.seasonalWinter?.dayOfWeeks" />
            <SeasonDurationDisplay :checkAvailableDate="checkValidity(data?.seasonalWinter)" :durations="data.seasonalWinter ? data.seasonalWinter?.durations : seasonal.seasonalWinter?.durations" />
          </div>
        </template>
        <template #filter="{ filterModel }">
          <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('management.package.departure_date_end')">
            <template #footer>
              <p class="text-center">
                <small>{{ $t('common.press_space') }}</small>
              </p>
            </template>
          </DatePicker>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="seasonalException" :showAddButton="false" :showFilterOperator="false" dataType="date">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ $t('common.labels.exception_season_dates') }}</span>
          </div>
        </template>
        <template #body="{ data }">
          <div v-if="data.seasonalException">
            <p class="mb-0 font-bold">
              {{
                checkValidity(data?.seasonalException)
                  ? formatDate(new Date(new Date().getFullYear(), +data?.seasonalException?.startMonth - 1, +data?.seasonalException?.startDay), 'dd-LLL', true, currentLanguage) +
                    ' ' +
                    $t('common.labels.to') +
                    ' ' +
                    formatDate(new Date(new Date().getFullYear(), +data?.seasonalException?.endMonth - 1, +data?.seasonalException?.endDay), 'dd-LLL', true, currentLanguage)
                  : ''
              }}
            </p>
            <SelectDatesDisplay :checkAvailableDate="checkValidity(data?.seasonalException)" class="my-2" :listDayOfWeek="data?.seasonalException ? data?.seasonalException?.dayOfWeeks : seasonal.seasonalException?.dayOfWeeks" />
            <SeasonDurationDisplay :checkAvailableDate="checkValidity(data?.seasonalException)" :durations="data.seasonalException ? data.seasonalException?.durations : seasonal.seasonalException?.durations" />
          </div>
        </template>
        <template #filter="{ filterModel }">
          <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('management.package.departure_date_end')">
            <template #footer>
              <p class="text-center">
                <small>{{ $t('common.press_space') }}</small>
              </p>
            </template>
          </DatePicker>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="minCost" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.cost') : $t('management.package.minimum') }}</span>
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.minimum') : $t('management.package.cost') }}</span>
          </div>
        </template>
      </TableColumn>
      <TableColumn class="cost-rule" field="costRule" :header="$t('management.package.apply_cost_rule')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <div class="flex justify-content-center">
            <Checkbox :modelValue="data.costRule" disabled :binary="true" />
          </div>
        </template>
      </TableColumn>
      <TableColumn class="rule-connector" field="connectorRule" :header="$t('management.package.apply_rule_connector')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <div class="flex justify-content-center">
            <Checkbox disabled :modelValue="data.connectorRule" :binary="true" />
          </div>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="estimatedArdTax" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ $t('management.package.estimated_ard_taxes') }}</span>
          </div>
        </template>
        <template #body="{ data }">
          {{ data.estimatedArdTax }}
        </template>
      </TableColumn>
      <TableColumn field="ruleStatus" :header="$t('common.status')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          {{ data.ruleStatus === STATUS_OPEN ? STATUS_OPEN : STATUS_CLOSED }}
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
            </template>
          </Select>
        </template>
      </TableColumn>
      <TableColumn class="min-w-200" field="validateMessage" :header="$t('management.hotel.message')" :showAddButton="false" :showFilterOperator="false" sortable>
        <template #sorticon>
          <i class="ml-2 sort_icon" :class="sortOrder === -1 ? 'pi pi-sort-amount-down-alt' : 'pi pi-sort-amount-up-alt'" @click="sortMessage()"></i>
        </template>
        <template #body="{ data, field }">
          <p class="mb-0 break-spaces">{{ handleShowMessage(data[field]) }}</p>
        </template>
      </TableColumn>
    </DataTable>
    <div class="flex">
      <div class="w-full text-center" v-if="type === 'IMPORT'">
        <Button id="btn_import_file" icon="pi pi-plus" @click="handleImportFile()" :label="$t('common.buttons.import_file_now')" data-testid="import-btn" class="import-button ml-4" />
      </div>
      <div class="w-full text-center" v-if="type === 'REVERT'">
        <Button id="btn_revert_file" icon="pi pi-plus" @click="handleRevert()" :label="$t('common.labels.revert_to_this_version')" class="revert-button ml-4" />
      </div>
      <div class="w-full text-center" v-if="type === 'EXPORT'">
        <ExportExcelFile id="export_btn" class="export ml-4" :sheets="sheetDataFlight" :columnsStyle="sheetColumnStyle" :file-name="renderNameFileExport('Flight_Instaquote_Rule_Management')"></ExportExcelFile>
      </div>
      <div>
        <Button id="btn_close" @click="closeModal()" :label="$t('common.buttons.close')" data-testid="cancel-btn" class="p-button" />
      </div>
    </div>
  </div>
</template>
